import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Edit,
  Calendar, 
  User as UserIcon, 
  Tag, 
  Image as ImageIcon, 
  Camera, 
  Upload, 
  X, 
  Loader2,
  CheckSquare,
  Square
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { db, auth, storage } from '../firebase';
import { collection, onSnapshot, query, orderBy, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { RichTextEditor } from '../components/RichTextEditor';
import { useTranslation } from '../hooks/useTranslation';

import { handleFirestoreError, OperationType } from '../lib/firestore-errors';

interface FAQItem {
  question: string;
  answer: string;
}

interface BlogPost {
  id?: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  date: string;
  imageUrl?: string;
  showImageInHeader?: boolean;
  seoTitle?: string;
  seoDescription?: string;
  keywords?: string;
  faqs?: FAQItem[];
  createdAt: any;
}

export function Blog() {
  const { t, isBrazil } = useTranslation();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingContent, setIsUploadingContent] = useState(false);
  const [toast, setToast] = useState<{ message: string, type: 'success' | 'error' } | null>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const contentImagesRef = useRef<HTMLInputElement>(null);
  const [newPost, setNewPost] = useState<Partial<BlogPost>>({
    title: '',
    excerpt: '',
    content: '',
    category: isBrazil ? 'Guia' : 'Guide',
    author: 'PlataPost Team',
    showImageInHeader: true,
    seoTitle: '',
    seoDescription: '',
    keywords: '',
    faqs: [],
    date: new Date().toLocaleDateString(isBrazil ? 'pt-BR' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  });

  const isAdmin = auth.currentUser?.email === "dewaldjordaan5@gmail.com";

  useEffect(() => {
    const q = query(collection(db, 'blog_posts'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as BlogPost));
      setPosts(data);
    }, (err) => {
      handleFirestoreError(err, OperationType.GET, 'blog_posts');
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !isAdmin) return;

    setIsUploading(true);
    try {
      const storageRef = ref(storage, `blog_images/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setNewPost(prev => ({ ...prev, imageUrl: url }));
    } catch (err) {
      console.error("Error uploading image:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleContentImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !isAdmin) return;

    setIsUploadingContent(true);
    try {
      const storageRef = ref(storage, `blog_images/${Date.now()}_content_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      // Automatically append image HTML to the content
      const imgTag = `<img src="${url}" alt="${file.name}" class="rounded-2xl max-w-full h-auto my-8" referrerPolicy="no-referrer" />\n<p class="text-xs text-center text-gray-400 italic font-medium">Caption: ${file.name}</p>\n\n`;
      setNewPost(prev => ({ ...prev, content: (prev.content || '') + imgTag }));
      setToast?.({ message: isBrazil ? "Imagem adicionada ao conteúdo!" : "Image added to content!", type: 'success' });
    } catch (err) {
      console.error("Error uploading content image:", err);
      setToast?.({ message: isBrazil ? "Falha ao enviar imagem." : "Failed to upload image.", type: 'error' });
    } finally {
      setIsUploadingContent(false);
      if (contentImagesRef.current) contentImagesRef.current.value = '';
    }
  };

  const handleAddPost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;
    try {
      if (isEditing && newPost.id) {
        const { id, ...updateData } = newPost;
        await updateDoc(doc(db, 'blog_posts', id), {
          ...updateData,
          updatedAt: new Date()
        });
        setToast?.({ message: isBrazil ? "Post atualizado!" : "Blog post updated!", type: 'success' });
        // Update selected post if it was the one edited
        if (selectedPost?.id === id) {
          setSelectedPost({ id, ...updateData } as BlogPost);
        }
      } else {
        await addDoc(collection(db, 'blog_posts'), {
          ...newPost,
          createdAt: new Date()
        });
        setToast?.({ message: isBrazil ? "Post publicado!" : "Blog post published!", type: 'success' });
      }
      
      setShowAddForm(false);
      setIsEditing(false);
      setNewPost({
        title: '',
        excerpt: '',
        content: '',
        category: isBrazil ? 'Guia' : 'Guide',
        author: 'PlataPost Team',
        showImageInHeader: true,
        seoTitle: '',
        seoDescription: '',
        keywords: '',
        date: new Date().toLocaleDateString(isBrazil ? 'pt-BR' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
        faqs: []
      });
    } catch (err) {
      console.error("Error saving blog post:", err);
      setToast?.({ message: isBrazil ? "Falha ao salvar post." : "Failed to save post.", type: 'error' });
    }
  };

  const handleEditPost = (post: BlogPost, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setNewPost(post);
    setIsEditing(true);
    setShowAddForm(true);
  };

  const handleDeletePost = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAdmin || !window.confirm(isBrazil ? "Tem certeza que deseja excluir este post?" : "Are you sure you want to delete this post?")) return;
    try {
      await deleteDoc(doc(db, 'blog_posts', id));
      if (selectedPost?.id === id) setSelectedPost(null);
    } catch (err) {
      console.error("Error deleting blog post:", err);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303]">
      <Helmet>
        <title>{selectedPost ? (selectedPost.seoTitle || selectedPost.title) : 'Blog | PlataPost AI'}</title>
        <meta name="description" content={selectedPost ? (selectedPost.seoDescription || selectedPost.excerpt) : (isBrazil ? "Estratégias e guias de IA para Redes Sociais." : "AI Social Media strategies and guides.")} />
        {selectedPost?.keywords && (
          <meta name="keywords" content={selectedPost.keywords} />
        )}
        {/* Open Graph / social */}
        {selectedPost && (
          <>
            <meta property="og:title" content={selectedPost.seoTitle || selectedPost.title} />
            <meta property="og:description" content={selectedPost.seoDescription || selectedPost.excerpt} />
            {selectedPost.imageUrl && <meta property="og:image" content={selectedPost.imageUrl} />}
            <meta property="og:type" content="article" />
            <meta name="twitter:card" content="summary_large_image" />
          </>
        )}
        {selectedPost?.faqs && selectedPost.faqs.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": selectedPost.faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })}
          </script>
        )}
      </Helmet>

      <main className="max-w-4xl mx-auto px-6 py-20 relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-indigo-600/5 blur-[120px] pointer-events-none" />
        {selectedPost ? (
          <motion.article 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-[3rem] border border-white/10 overflow-hidden shadow-2xl"
          >
            {selectedPost.showImageInHeader && selectedPost.imageUrl && (
              <div className="w-full h-[450px] relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#030303]/80 z-10" />
                <img 
                  src={selectedPost.imageUrl} 
                  alt={selectedPost.title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                  referrerPolicy="no-referrer"
                />
              </div>
            )}
              <div className="p-8 md:p-16 relative z-20">
                <button 
                  onClick={() => setSelectedPost(null)}
                  className="mb-12 flex items-center gap-3 text-indigo-400 font-black uppercase text-xs tracking-widest hover:text-indigo-300 transition-all group"
                >
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                  {isBrazil ? "Voltar para o Blog" : "Back to Blog"}
                </button>
                <header className="mb-12">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-4">
                    <span className="px-4 py-1.5 bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 rounded-full text-[10px] font-black uppercase tracking-widest">{selectedPost.category}</span>
                    <span className="text-sm text-gray-500 font-medium">{selectedPost.date}</span>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={(e) => handleEditPost(selectedPost, e)}
                        className="p-3 bg-white/5 text-white rounded-2xl hover:bg-white/10 border border-white/10 transition-colors"
                        title={isBrazil ? "Editar Post" : "Edit Post"}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={(e) => handleDeletePost(selectedPost.id!, e)}
                        className="p-3 bg-red-500/10 text-red-400 rounded-2xl hover:bg-red-500/20 border border-red-500/20 transition-colors"
                        title={isBrazil ? "Excluir Post" : "Delete Post"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <h1 className="text-4xl md:text-6xl font-black text-white tracking-tighter leading-tight mb-8">
                  {selectedPost.title}
                </h1>
                <p className="text-xl text-gray-400 leading-relaxed max-w-3xl font-medium">
                  {selectedPost.excerpt}
                </p>
              </header>

              <div className="prose prose-lg prose-invert prose-indigo max-w-none text-gray-300">
                <div dangerouslySetInnerHTML={{ __html: selectedPost.content }} />
              </div>

              {selectedPost.faqs && selectedPost.faqs.length > 0 && (
                <div className="mt-20 pt-16 border-t border-white/5">
                  <h3 className="text-3xl font-black text-white mb-10 tracking-tight uppercase">{isBrazil ? "Perguntas Frequentes" : "Frequently Asked Questions"}</h3>
                  <div className="space-y-6">
                    {selectedPost.faqs.map((faq, idx) => (
                      <div 
                        key={idx} 
                        className={`bg-white/5 rounded-3xl border transition-all ${openFaqIndex === idx ? 'border-indigo-500/30 ring-1 ring-indigo-500/30' : 'border-white/5'}`}
                      >
                        <button
                          onClick={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)}
                          className="w-full flex items-center justify-between p-8 text-left"
                        >
                          <span className="text-lg font-black text-white pr-6 tracking-tight leading-snug">{faq.question}</span>
                          <motion.div
                            animate={{ rotate: openFaqIndex === idx ? 135 : 0 }}
                            className="text-indigo-400 shrink-0"
                          >
                            <Plus className="w-6 h-6 transition-transform" />
                          </motion.div>
                        </button>
                        <AnimatePresence>
                          {openFaqIndex === idx && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="p-8 pt-0 text-gray-400 font-medium leading-relaxed border-t border-white/5">
                                {faq.answer}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <footer className="mt-20 pt-10 border-t border-white/5 flex items-center gap-6">
                <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                  <UserIcon className="w-7 h-7 text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm font-black text-white uppercase tracking-widest leading-none mb-1">{selectedPost.author}</p>
                  <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest">{isBrazil ? "Equipe de Especialistas PlataPost" : "PlataPost Expert Contributors"}</p>
                </div>
              </footer>
            </div>
          </motion.article>
        ) : (
          <div className="space-y-8">
            <header className="text-center mb-20 space-y-4">
              <p className="text-indigo-500 font-bold uppercase tracking-widest text-sm">
                Blog & News
              </p>
              <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter">
                Insights & <span className="text-indigo-500">Strategies.</span>
              </h1>
              <p className="text-xl text-gray-400 font-medium mb-6">{isBrazil ? "Domine a arte do crescimento nas redes sociais impulsionado por IA." : "Master the art of AI-powered social media growth."}</p>
              {isAdmin && posts.length > 0 && (
                <button 
                  onClick={() => setShowAddForm(true)}
                  className="mx-auto mt-8 px-8 py-3 bg-indigo-600 text-white rounded-2xl font-black hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-600/20 uppercase tracking-widest text-xs flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {isBrazil ? "Criar Novo Post" : "Create New Post"}
                </button>
              )}
            </header>

            {posts.length === 0 ? (
              <div className="text-center py-24 glass-card rounded-[3rem] border border-dashed border-white/10">
                <p className="text-gray-500 font-medium">No blog posts yet.</p>
                {isAdmin && (
                  <button 
                    onClick={() => setShowAddForm(true)}
                    className="mt-6 px-8 py-3 bg-indigo-600 text-white rounded-2xl font-black hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-600/20 uppercase tracking-widest text-xs"
                  >
                    Write the first one
                  </button>
                )}
              </div>
            ) : (
              <div className="grid gap-8">
                {posts.map(post => (
                  <motion.div 
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => setSelectedPost(post)}
                    className="group glass-card rounded-[2.5rem] border border-white/5 hover:border-white/10 transition-all cursor-pointer relative flex flex-col md:flex-row overflow-hidden shadow-2xl shadow-black/50"
                  >
                    {post.imageUrl && (
                      <div className="md:w-72 h-56 md:h-auto overflow-hidden shrink-0 relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent z-10" />
                        <img 
                          src={post.imageUrl} 
                          alt={post.title} 
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    )}
                    <div className="p-10 flex-1 relative z-20">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                          <span className="px-3 py-1 bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 rounded-full text-[10px] font-black uppercase tracking-widest">{post.category}</span>
                          <span className="text-xs text-gray-500 font-medium">{post.date}</span>
                        </div>
                        {isAdmin && (
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                              onClick={(e) => handleEditPost(post, e)}
                              className="p-2 bg-white/5 text-white rounded-full hover:bg-white/10 border border-white/10"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={(e) => handleDeletePost(post.id!, e)}
                              className="p-2 bg-red-500/10 text-red-400 rounded-full hover:bg-red-500/20 border border-red-500/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                      <h2 className="text-3xl font-black text-white mb-4 tracking-tight group-hover:text-indigo-400 transition-colors leading-tight">{post.title}</h2>
                      <p className="text-gray-400 line-clamp-2 mb-8 font-medium leading-relaxed">{post.excerpt}</p>
                      <div className="flex items-center justify-between pt-6 border-t border-white/5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                            <UserIcon className="w-5 h-5 text-indigo-400" />
                          </div>
                          <span className="text-sm font-black text-gray-300 uppercase tracking-tight">{post.author}</span>
                        </div>
                        <span className="text-indigo-400 font-black text-xs uppercase tracking-widest group-hover:translate-x-2 transition-transform inline-flex items-center gap-2">
                          {isBrazil ? "Ler Mais" : "Read More"} <ArrowLeft className="w-4 h-4 rotate-180" />
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Add Form Modal */}
      <AnimatePresence>
        {showAddForm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-6 overflow-y-auto">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#0a0a0a] rounded-[2.5rem] shadow-2xl w-full max-w-2xl my-auto overflow-hidden border border-white/10"
            >
              <div className="p-8 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
                <h2 className="text-xl font-black text-white uppercase tracking-tight">{isEditing ? (isBrazil ? 'Editar Post' : 'Edit Blog Post') : (isBrazil ? 'Criar Novo Post' : 'Create New Blog Post')}</h2>
                <button onClick={() => { setShowAddForm(false); setIsEditing(false); }} className="p-2 border border-white/10 rounded-xl text-gray-500 hover:text-white hover:bg-white/5 transition-all">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleAddPost} className="p-10 space-y-8 max-h-[75vh] overflow-y-auto custom-scrollbar">
                {/* Image Upload Area */}
                <div className="space-y-4">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{isBrazil ? 'Imagem de Capa' : 'Cover Image'}</label>
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative w-full h-56 rounded-3xl border-2 border-dashed transition-all flex flex-col items-center justify-center gap-4 cursor-pointer overflow-hidden ${
                      newPost.imageUrl ? 'border-indigo-500 bg-indigo-500/5' : 'border-white/10 hover:border-indigo-500/50 hover:bg-white/5'
                    }`}
                  >
                    {isUploading ? (
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
                        <span className="text-sm font-black text-gray-500 uppercase tracking-widest">{isBrazil ? 'Enviando...' : 'Uploading...'}</span>
                      </div>
                    ) : newPost.imageUrl ? (
                      <>
                        <img src={newPost.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity backdrop-blur-sm">
                          <Camera className="w-10 h-10 text-white" />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center border border-white/5 shadow-inner">
                          <ImageIcon className="w-8 h-8 text-gray-500" />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-black text-white uppercase tracking-tight">{isBrazil ? 'Clique para enviar imagem' : 'Click to upload cover image'}</p>
                          <p className="text-xs text-gray-500 mt-2 font-medium tracking-tight">PNG, JPG or WebP (Max 5MB)</p>
                          <p className="text-[9px] text-indigo-400 font-black uppercase tracking-widest mt-4 bg-indigo-500/10 px-3 py-1 rounded-full border border-indigo-500/20 inline-block">{isBrazil ? 'Recomendado: 1200x675 (16:9)' : 'Recommended: 1200x675 (16:9)'}</p>
                        </div>
                      </>
                    )}
                    <input 
                      type="file" 
                      ref={fileInputRef} 
                      onChange={handleImageUpload} 
                      className="hidden" 
                      accept="image/*" 
                    />
                  </div>

                  {newPost.imageUrl && (
                    <div className="flex items-center gap-4 mt-6 p-6 bg-white/5 rounded-[2rem] border border-white/10">
                      <button
                        type="button"
                        onClick={() => setNewPost(prev => ({ ...prev, showImageInHeader: !prev.showImageInHeader }))}
                        className="flex items-center gap-3 group"
                      >
                        {newPost.showImageInHeader ? (
                          <div className="w-6 h-6 bg-indigo-600 rounded-lg flex items-center justify-center border border-indigo-500">
                             <CheckSquare className="w-4 h-4 text-white" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 border-2 border-white/10 rounded-lg group-hover:border-indigo-500/50 transition-colors" />
                        )}
                        <span className="text-sm font-black text-gray-300 uppercase tracking-tight">{isBrazil ? 'Exibir esta imagem no cabeçalho do artigo' : 'Display this image in the article header'}</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{isBrazil ? 'Categoria' : 'Category'}</label>
                      <input 
                        type="text"
                        value={newPost.category}
                        onChange={e => setNewPost({...newPost, category: e.target.value})}
                        placeholder="e.g. Guide, Strategy"
                        className="w-full px-6 py-4 rounded-2xl bg-white/5 border border-white/5 focus:bg-white/10 focus:ring-2 focus:ring-indigo-600 outline-none transition-all font-bold text-white placeholder:text-gray-700"
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{isBrazil ? 'Autor' : 'Author'}</label>
                      <input 
                        type="text"
                        value={newPost.author}
                        onChange={e => setNewPost({...newPost, author: e.target.value})}
                        placeholder="PlataPost Team"
                        className="w-full px-6 py-4 rounded-2xl bg-white/5 border border-white/5 focus:bg-white/10 focus:ring-2 focus:ring-indigo-600 outline-none transition-all font-bold text-white placeholder:text-gray-700"
                        required
                      />
                    </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{isBrazil ? 'Título' : 'Title'}</label>
                  <input 
                    type="text"
                    value={newPost.title}
                    onChange={e => setNewPost({...newPost, title: e.target.value})}
                    placeholder={isBrazil ? "Insira um título envolvente..." : "Enter an engaging title..."}
                    className="w-full px-6 py-5 rounded-2xl bg-white/5 border border-white/5 focus:bg-white/10 focus:ring-2 focus:ring-indigo-600 outline-none transition-all font-black text-2xl text-white tracking-tight placeholder:text-gray-700"
                    required
                  />
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{isBrazil ? 'Resumo' : 'Excerpt'}</label>
                  <textarea 
                    value={newPost.excerpt}
                    onChange={e => setNewPost({...newPost, excerpt: e.target.value})}
                    placeholder={isBrazil ? "Forneça um resumo para o feed..." : "Provide a hooks-driven summary for the feed..."}
                    className="w-full px-6 py-4 rounded-2xl bg-white/5 border border-white/5 focus:bg-white/10 focus:ring-2 focus:ring-indigo-600 outline-none transition-all min-h-[100px] font-medium text-white placeholder:text-gray-700 leading-relaxed resize-none"
                    required
                  />
                </div>

                <div className="bg-indigo-600/5 rounded-[2.5rem] p-10 border border-indigo-500/10 space-y-8 shadow-inner">
                  <h3 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] flex items-center gap-3">
                    <Tag className="w-4 h-4" />
                    Search Engine Optimization (SEO)
                  </h3>
                  
                  <div className="grid gap-8">
                    <div className="space-y-3">
                      <label className="text-[9px] font-black text-gray-600 uppercase tracking-widest ml-1">SEO Title (Overrides main title for Google)</label>
                      <input 
                        type="text"
                        value={newPost.seoTitle}
                        onChange={e => setNewPost({...newPost, seoTitle: e.target.value})}
                        placeholder="Ideally 50-60 characters..."
                        className="w-full px-5 py-3 rounded-xl bg-white/[0.03] border border-white/5 focus:bg-white/5 focus:ring-2 focus:ring-indigo-600 outline-none transition-all font-black text-white text-sm placeholder:text-gray-800"
                      />
                    </div>

                    <div className="space-y-3">
                      <label className="text-[9px] font-black text-gray-600 uppercase tracking-widest ml-1">SEO Description</label>
                      <textarea 
                        value={newPost.seoDescription}
                        onChange={e => setNewPost({...newPost, seoDescription: e.target.value})}
                        placeholder="Ideally 150-160 characters..."
                        className="w-full px-5 py-3 rounded-xl bg-white/[0.03] border border-white/5 focus:bg-white/5 focus:ring-2 focus:ring-indigo-600 outline-none transition-all min-h-[80px] font-medium text-white text-sm placeholder:text-gray-800 leading-relaxed resize-none"
                      />
                    </div>

                    <div className="space-y-3">
                      <label className="text-[9px] font-black text-gray-600 uppercase tracking-widest ml-1">Keywords (Comma separated)</label>
                      <input 
                        type="text"
                        value={newPost.keywords}
                        onChange={e => setNewPost({...newPost, keywords: e.target.value})}
                        placeholder="e.g. AI tools, Branding, Social Media Strategy"
                        className="w-full px-5 py-3 rounded-xl bg-white/[0.03] border border-white/5 focus:bg-white/5 focus:ring-2 focus:ring-indigo-600 outline-none transition-all font-bold text-white text-sm placeholder:text-gray-800"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{isBrazil ? 'Conteúdo' : 'Content'}</label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => contentImagesRef.current?.click()}
                        disabled={isUploadingContent}
                        className="text-[9px] font-black text-white uppercase tracking-widest bg-white/10 px-4 py-2 rounded-xl hover:bg-white/20 transition-all flex items-center gap-2 disabled:opacity-50 border border-white/10"
                      >
                        {isUploadingContent ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Upload className="w-3.5 h-3.5" />
                        )}
                        {isBrazil ? 'Inserir Imagem' : 'Insert Image'}
                      </button>
                      <input 
                        type="file" 
                        ref={contentImagesRef} 
                        onChange={handleContentImageUpload} 
                        className="hidden" 
                        accept="image/*" 
                      />
                      <span className="text-[9px] font-black text-indigo-400 uppercase tracking-[0.15em] bg-indigo-500/10 px-4 py-2 rounded-xl border border-indigo-500/20">Rich Text Editor</span>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-3xl overflow-hidden border border-white/5">
                    <RichTextEditor 
                      content={newPost.content || ''}
                      onChange={(html) => setNewPost({...newPost, content: html})}
                      placeholder={isBrazil ? "Escreva sua obra-prima... Selecione o texto para revelar o menu de formatação rápida." : "Write your masterpiece... Select text to reveal the quick formatting menu."}
                    />
                  </div>
                </div>

                <div className="space-y-6 pt-6 border-t border-white/5">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">FAQs (SEO Boost)</label>
                    <button
                      type="button"
                      onClick={() => {
                        const faqs = [...(newPost.faqs || [])];
                        faqs.push({ question: '', answer: '' });
                        setNewPost({ ...newPost, faqs });
                      }}
                      className="text-[9px] font-black text-white uppercase tracking-widest bg-indigo-600 px-4 py-2 rounded-xl hover:bg-indigo-700 transition-all flex items-center gap-2 shadow-lg shadow-indigo-600/20"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {isBrazil ? 'Adicionar Pergunta' : 'Add Question'}
                    </button>
                  </div>
                  
                  <div className="space-y-6">
                    {(newPost.faqs || []).map((faq, idx) => (
                      <div key={idx} className="p-8 bg-white/[0.02] rounded-3xl border border-white/5 space-y-6 relative group/faq shadow-inner">
                        <button
                          type="button"
                          onClick={() => {
                            const faqs = (newPost.faqs || []).filter((_, i) => i !== idx);
                            setNewPost({ ...newPost, faqs });
                          }}
                          className="absolute top-6 right-6 text-gray-600 hover:text-red-400 transition-colors opacity-0 group-hover/faq:opacity-100"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        <div className="space-y-3">
                          <label className="text-[9px] font-black text-gray-600 uppercase tracking-widest ml-1">{isBrazil ? 'Pergunta' : 'Question'}</label>
                          <input
                            type="text"
                            value={faq.question}
                            onChange={(e) => {
                              const faqs = [...(newPost.faqs || [])];
                              faqs[idx].question = e.target.value;
                              setNewPost({ ...newPost, faqs });
                            }}
                            placeholder="e.g. How does Brand Brain work?"
                            className="w-full px-5 py-3 rounded-xl bg-white/[0.03] border border-white/5 focus:bg-white/5 focus:ring-2 focus:ring-indigo-600 outline-none transition-all font-black text-white text-sm placeholder:text-gray-800"
                          />
                        </div>
                        <div className="space-y-3">
                          <label className="text-[9px] font-black text-gray-600 uppercase tracking-widest ml-1">{isBrazil ? 'Resposta' : 'Answer'}</label>
                          <textarea
                            value={faq.answer}
                            onChange={(e) => {
                              const faqs = [...(newPost.faqs || [])];
                              faqs[idx].answer = e.target.value;
                              setNewPost({ ...newPost, faqs });
                            }}
                            placeholder={isBrazil ? "Escreva uma resposta concisa..." : "Write a concise answer..."}
                            className="w-full px-5 py-3 rounded-xl bg-white/[0.03] border border-white/5 focus:bg-white/5 focus:ring-2 focus:ring-indigo-600 outline-none transition-all min-h-[80px] font-medium text-white text-sm placeholder:text-gray-800 leading-relaxed resize-none"
                          />
                        </div>
                      </div>
                    ))}
                    {(newPost.faqs || []).length === 0 && (
                      <p className="text-[10px] text-center text-gray-600 font-bold uppercase tracking-widest py-8 border border-dashed border-white/5 rounded-3xl">{isBrazil ? "Nenhuma FAQ adicionada ainda." : "No FAQs added yet."}</p>
                    )}
                  </div>
                </div>

                <div className="pt-8 sticky bottom-0 bg-[#0a0a0a] pb-2 border-t border-white/5 flex gap-4 mt-12">
                  <button 
                    type="button"
                    onClick={() => { setShowAddForm(false); setIsEditing(false); }}
                    className="flex-1 px-8 py-5 rounded-2xl font-black uppercase tracking-widest text-[10px] bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white transition-all border border-white/10"
                  >
                    {isBrazil ? 'Cancelar' : 'Cancel'}
                  </button>
                  <button 
                    type="submit"
                    disabled={isUploading}
                    className="flex-[2] bg-indigo-600 text-white py-5 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-600/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 border border-indigo-500"
                  >
                    {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : isEditing ? <CheckSquare className="w-5 h-5" /> : <Upload className="w-5 h-5" />}
                    {isEditing ? (isBrazil ? 'Salvar Alterações' : 'Save Changes') : (isBrazil ? 'Publicar no Feed' : 'Publish to Feed')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-[200] px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3 font-bold border ${
              toast.type === 'success' ? 'bg-indigo-600 text-white border-indigo-500' : 'bg-red-600 text-white border-red-500'
            }`}
          >
            {toast.type === 'success' ? <CheckSquare className="w-5 h-5" /> : <X className="w-5 h-5" />}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
